import { EffectCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { playlistApi } from '../../api/swagger'
import { useAppSnackbar } from '../useAppSnackbar'
import useLoadingContext from '../useLoadingContext'
import { useLoadingIds } from '../useLoadingIds'

interface UseRemoveSharedPlaylistProps {
  playlistKey: number
  userKey: string
  email: string
  onSuccess?: () => void
}

export const useRemoveSharedPlaylist = ({
  playlistKey,
  userKey,
  email,
  onSuccess,
}: UseRemoveSharedPlaylistProps): {
  removePlaylist: EffectCallback
} => {
  const { t } = useTranslation()
  const { handleError, handleMessage } = useAppSnackbar()

  const {
    Playlist: { unsharePlaylist: unsharePlaylistLoadingId },
  } = useLoadingIds()

  const defaultErrorMessage = t(
    'UseRemoveSharedPlaylist.Message.DeletePlaylistFailure',
    'An unknown error occurred while attempting to remove the playlist.'
  )

  const removeSharedPlaylist = async () => {
    if (!playlistKey) return

    try {
      await playlistApi.unsharePlaylist({
        playlistKey,
        body: {
          email,
          userKey,
        },
      })

      handleMessage(
        t(
          'UseRemoveSharedPlaylist.Message.DeletePlaylistSuccess',
          'Playlist has successfully been deleted.'
        )
      )
      onSuccess?.()
    } catch (error) {
      await handleError(error, defaultErrorMessage)
    }
  }

  const { triggerFetch } = useLoadingContext({
    asyncFunction: removeSharedPlaylist,
    loadingId: unsharePlaylistLoadingId,
  })

  return { removePlaylist: triggerFetch }
}
